"use client"
import styles from "@@/styles/components/contact/ContactForm.module.scss";

import { ralewayRegular, exo2SemiBold } from "@@/utils/fonts";
import {JSX, useState} from "react";
import {usePathname, useRouter} from "next/navigation";
import {getRecaptchaTokenSafe} from "@@/utils/recaptcha";


interface Input {
	label: string;
	isRequired?: boolean;
	input_type: string;
	id?: string;
	name: string;
	error_message?: string;
	placeholder?: string;
}

interface IContactFormProps {
	fields: {
		title: string,
		inputs: Input[];
	}[];
	buttonTxt: string;
	className?: string;
	locale: string;
}

export default function ContactForm({fields, buttonTxt, className, locale}: IContactFormProps): JSX.Element {

	const router = useRouter();

	const [inputValues, setInputValues] = useState<Record<string, string>>({});

	const [dirtyInputs, setDirtyInputs] = useState<Record<string, boolean>>({});

	const handleInputChange = (name: string, value: string) => {
		setDirtyInputs((prev) => ({
			...prev,
			[name]: value.trim().length > 0,
		}));
		setInputValues((prev) => ({
			...prev,
			[name]: value,
		}));
	};
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const website = (inputValues["website"] || "").trim();
		if (website) {
			router.push(`/${locale}/form-submitted?error=bot`);
			return;
		}

		const token = await getRecaptchaTokenSafe("contact_form");
		console.log("token", token);
		if (!token) {
			router.push(`/${locale}/form-submitted?error=recaptcha`);
			return;
		}

		const payload = {
			locale,
			token,
			user_name: inputValues["user_name"] || "",
			user_company: inputValues["user_company"] || "",
			user_email: inputValues["user_email"] || "",
			user_phone_number: inputValues["user_phone_number"] || "",
			user_subject: inputValues["user_subject"] || "",
			user_message: inputValues["user_message"] || "",
		};

		let res: Response;
		try {
			res = await fetch("/api/contact", {
				method: "POST",
				headers: {"Content-Type": "application/json"},
				body: JSON.stringify(payload),
			});
		} catch {
			router.push(`/${locale}/form-submitted?error=network`);
			return;
		}

		const data = await res.json().catch(() => null);
		if (res.ok && data?.success) {
			router.push(`/${locale}/form-submitted?success`);
		} else {
			router.push(`/${locale}/form-submitted?error`);
		}
	};


	return (
		<form className={`${ralewayRegular.className} ${styles.main} ${className} d-flex flex-column gap-2`} onSubmit={handleSubmit}>
			<input type="hidden" name="locale" value={locale} />
			<input type="text"
				name="website"
				className="d-none"
				autoComplete="off"
				tabIndex={-1}
				aria-hidden="true"
				onChange={e => handleInputChange("website", e.target.value)}
				value={inputValues["website"] || ""}
			/>
			{fields.map((field, fieldIndex) => (
				<fieldset key={fieldIndex}>
					<h2 className={`${exo2SemiBold.className}`}>{field.title}</h2>
					<ul>
						{field.inputs.map((input, inputIndex) => {
							const isDirty = dirtyInputs[input.name] ?? false;
							return (
								<li
									key={inputIndex}
									className={`form-input ${input.input_type} ${isDirty ? 'dirty' : ''}`}
								>
									<div className={`label`} onClick={
										(e)=> {
											e.preventDefault();
											const input$ = document.querySelector(`#${input.id}`) as HTMLInputElement;
											input$.focus()
										}}>
										{input.input_type === 'radio' && (
											<span className={`radio-indicator`}></span>
										)}
										<label htmlFor={input.name}>{input.label}</label>
										{input.isRequired && input.input_type !== 'radio' && (
											<span className={styles.bold}>
												<span className={styles.required}>*</span>
											</span>
										)}
									</div>

									{input.input_type === 'textarea' ? (
										<textarea
											id={input.id}
											name={input.name}
											required={input.isRequired}
											onChange={(e) => handleInputChange(input.name, e.target.value)}
											value={inputValues[input.name] || ''}
										/>
									) : input.input_type === 'radio' ? (
										<input
											type="radio"
											id={input.id}
											name={input.name}
											value={input.label}
											required={input.isRequired}
											checked={inputValues[input.name] === input.label}
											onChange={(e) => handleInputChange(input.name, input.label)}
										/>
									) : (
										<input
											type={input.input_type}
											id={input.id}
											name={input.name}
											required={input.isRequired}
											value={inputValues[input.name] || ''}
											onChange={(e) => handleInputChange(input.name, e.target.value)}
										/>
									)}
								</li>
							);
						})}
					</ul>
				</fieldset>
			))}

			<button type="submit" className="w-fit min-w-100-mobile">{buttonTxt}</button>
		</form>
	);
}
